import { NodeEditor, ClassicPreset } from 'rete'
import { AreaPlugin, AreaExtensions } from 'rete-area-plugin'
import {
  ConnectionPlugin,
  Presets as ConnectionPresets,
  ClassicFlow,
  getSourceTarget,
} from 'rete-connection-plugin'
import { ReactPlugin } from 'rete-react-plugin'
import { type SocketData } from 'rete-connection-plugin/_types/types'
import { type PipelineEditorGraphInput } from '../types.ts'
import { type PipelineEditorProps } from '../PipelineEditor.tsx'
import { Instacart_Catalog_Catflow_V1_StepCategory } from '../../../../../api'
import { PipelineStepType } from '../../../../../types/attributeManagement.types.ts'
import {
  buildNodeSockets,
  handleNodeConnectionAdded,
  handleNodeConnectionRemoved,
  PipelineEditorStepNode,
} from './nodes.tsx'
import { setupRenderer } from './renderer.tsx'
import { addCustomBackground } from './background.tsx'
import { arrangeNodes } from './arrange.tsx'
import { ReadonlyPlugin } from './plugins/readonly.tsx'
import {
  type AreaExtra,
  Connection,
  type NodeProps,
  type PipelineEditorNodeError,
  type Schemes,
} from './types.ts'
import { Cycles } from './plugins/cycles.tsx'

export async function createEditor(
  container: HTMLElement,
  graphInput: PipelineEditorGraphInput | null,
  props: PipelineEditorProps,
  onInvalidConnection: () => void
) {
  const socket: ClassicPreset.Socket = new ClassicPreset.Socket('socket')
  const editor = new NodeEditor<Schemes>()
  const readonly = new ReadonlyPlugin<Schemes>()
  const area = new AreaPlugin<Schemes, AreaExtra>(container)
  const connection = new ConnectionPlugin<Schemes, AreaExtra>()
  const render = new ReactPlugin<Schemes, AreaExtra>()
  const nodeMap: { [pipeLineStepId: string]: NodeProps } = {}
  const connectionCache: {
    [pipeLineStepId: string]: {
      inputs: string[]
      outputs: string[]
    }
  } = {}
  const cycles = new Cycles()

  /**
   * Setup plugins
   */
  connection.addPreset(
    () =>
      new ClassicFlow({
        canMakeConnection(from: SocketData, to: SocketData) {
          const [source, target] = getSourceTarget(from, to) || [null, null]
          if (!source || !target) return false

          const sourceNode = nodeMap[source.nodeId] as PipelineEditorStepNode
          const targetNode = nodeMap[target.nodeId] as PipelineEditorStepNode
          if (!sourceNode && !targetNode) return false

          const hasNoCycles = cycles.addEdge(
            graphInput?.pipelineStepIdToStepMap,
            sourceNode.id,
            targetNode.id
          )
          if (!hasNoCycles) {
            onInvalidConnection()
            return false
          }

          const existingPipelineNextSteps =
            sourceNode?.input?.step?.nextStepsRule?.possibleNextSteps ?? []
          const isAlreadyAChild = existingPipelineNextSteps.includes(targetNode.id)
          return !isAlreadyAChild
        },
        makeConnection(from, to) {
          const [source, target] = getSourceTarget(from, to) || [null, null]
          if (!source || !target) return false

          const sourceIndex = Number.parseInt(source.key.split('_')[1])
          const sourceNode = nodeMap[source.nodeId] as PipelineEditorStepNode
          const targetIndex = Number.parseInt(source.key.split('_')[1])
          const targetNode = nodeMap[target.nodeId] as PipelineEditorStepNode
          if (!sourceNode && !targetNode) return false
          const modifiedStep = handleNodeConnectionAdded(
            sourceNode,
            sourceIndex,
            targetNode,
            targetIndex
          )
          props.onChange(modifiedStep)
          return true
        },
      })
  )
  AreaExtensions.selectableNodes(area, AreaExtensions.selector(), {
    accumulating: AreaExtensions.accumulateOnCtrl(),
  })
  AreaExtensions.simpleNodesOrder(area)
  connection.addPreset(ConnectionPresets.classic.setup())
  editor.use(readonly.root)
  editor.use(area)
  editor.addPipe(context => {
    if (context.type === 'connectionremove') {
      const { data: connection } = context

      const sourceNode = nodeMap[connection.source] as PipelineEditorStepNode
      const targetNode = nodeMap[connection.target] as PipelineEditorStepNode
      if (!sourceNode && !targetNode) return

      const sourceIndex = Number.parseInt(connection.sourceOutput.split('_')[1])
      const targetIndex = Number.parseInt(connection.targetInput.split('_')[1])

      const modifiedStep = handleNodeConnectionRemoved(
        sourceNode,
        sourceIndex,
        targetNode,
        targetIndex
      )
      props.onChange(modifiedStep)
      return
    }
    return context
  })
  area.use(readonly.area)
  area.use(render)
  area.use(connection)

  /**
   * Setup custom plugins
   */

  setupRenderer(render)
  addCustomBackground(area)

  /**
   * Helpers
   */

  const asyncBuildGraph = async (
    pipelineStepId: string | null,
    graphInput: PipelineEditorGraphInput,
    nodeCache: { [pipelineStepId: string]: NodeProps },
    connectionCache: {
      [pipeLineStepId: string]: {
        inputs: string[]
        outputs: string[]
      }
    }
  ): Promise<NodeProps | null> => {
    const details = pipelineStepId ? graphInput?.pipelineStepIdToStepMap[pipelineStepId] : null
    if (!pipelineStepId || !details || !details?.step) return null
    if (nodeCache[pipelineStepId]) return nodeCache[pipelineStepId]

    const step = details.step
    const stepVersion = details?.stepVersion

    const nextStepPipelineIds = step.nextStepsRule?.possibleNextSteps ?? []
    const label = step.pipelineStepId ?? ''
    const node = new PipelineEditorStepNode(label, {
      step,
      stepVersion: stepVersion,
      props,
    })
    node.id = pipelineStepId
    nodeCache[pipelineStepId] = node
    connectionCache[pipelineStepId] = { inputs: [], outputs: [] }
    buildNodeSockets(step, node, socket)

    let emptyChildren = 0
    for (const pipelineStepId of nextStepPipelineIds) {
      const childNode = await asyncBuildGraph(
        pipelineStepId,
        graphInput,
        nodeCache,
        connectionCache
      )
      if (!childNode || !(childNode instanceof PipelineEditorStepNode)) {
        emptyChildren += 1
        continue
      }

      const parentConnections = connectionCache[node.id]
      const childConnections = connectionCache[childNode.id]

      const childInputId: string = `input_${childConnections.inputs.length}`
      const parentOutputId: string = `output_${parentConnections.outputs.length + emptyChildren}`

      cycles.addEdge(graphInput.pipelineStepIdToStepMap, node.id, childNode.id)
      await editor.addConnection(
        new Connection(node as NodeProps, parentOutputId, childNode as NodeProps, childInputId)
      )
      parentConnections.outputs.push(node.id)
      childConnections.inputs.push(childNode.id)
    }

    // We must do this as late as possible as it triggers react code
    // Each time we edit the node it triggers a full re-render (dismount then remount), meaning if
    // the node has a http call it will repeatedly do it.
    await editor.addNode(node)
    return node as NodeProps
  }

  const arrange = async () => {
    const numberOfGraphs = await arrangeNodes(nodeMap, area)
    await AreaExtensions.zoomAt(area, editor.getNodes())

    return numberOfGraphs
  }

  const runValidations = (rootNodes: NodeProps[]) => {
    const errors: PipelineEditorNodeError[] = []

    // There must only be 1 root and that root must be an input.
    const hasSingleRootNode =
      rootNodes.length === 1 &&
      rootNodes[0] instanceof PipelineEditorStepNode &&
      rootNodes[0].input?.stepVersion?.step?.stepCategory ===
        Instacart_Catalog_Catflow_V1_StepCategory.Input

    if (!hasSingleRootNode) {
      errors.push({
        message: 'Pipeline should only have 1 root step that is an input.',
      })
    }

    for (const nodeProp of Object.values(nodeMap)) {
      const stepNode = nodeProp as PipelineEditorStepNode
      const nextStepType = stepNode?.input?.step?.type ?? PipelineStepType.Task
      const isControl = nextStepType.toLowerCase() === PipelineStepType.Control.toLowerCase()
      if (isControl) {
        const possibleSteps = stepNode?.input?.step?.nextStepsRule?.possibleNextSteps ?? []
        if (possibleSteps.includes('')) {
          errors.push({
            pipelineStepId: stepNode.id,
            message: 'Missing an output connection',
          })
        }
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    }
  }

  /**
   * Process inputs
   */
  const processGraphInput = async () => {
    if (!graphInput) return

    for (const pipelineStepId in graphInput.pipelineStepIdToStepMap) {
      await asyncBuildGraph(pipelineStepId, graphInput, nodeMap, connectionCache)
    }
    if (Object.values(nodeMap).length == 0) return
    const rootNodes = await arrange()
    if (props.disabled) {
      readonly.enable()
    }
    props.onProcessed(runValidations(rootNodes))
  }
  await processGraphInput()

  return {
    editor,
    area,
    arrange,
    render,
    socket,
    destroy: () => area.destroy(),
  }
}
